import { useRef, useState } from 'react';

import Carousel, { type CarouselRef } from 'components/Carousel';
import Img from 'components/Img';
import ThumbnailList from 'components/ThumbnailList';
import type { VariantResponse } from 'models/api';
import { getAllImages } from 'utils/business-logic';
import { useI18n } from 'utils/i18n';
import { clampIndex } from 'utils/math';

interface Props {
	className?: string;
	product: VariantResponse;
}

export default function ProductPopoverImage({ className, product }: Props) {
	const { t } = useI18n();
	const carouselRef = useRef<CarouselRef>(null);
	const images = getAllImages(product);

	const [selectedIndex, setSelectedIndex] = useState(0);
	const setIndex = (index: number) => {
		setSelectedIndex(clampIndex(index, images.length));
	};

	return (
		<div className={className}>
			<Carousel
				ref={carouselRef}
				controlsLayout="inline-floating"
				hasFocusableList
				hasProgressTracker={false}
				nextButtonLabel={t('general_carousel_next_image_button_label')}
				prevButtonLabel={t('general_carousel_previous_image_button_label')}
				carouselClassName="flex gap-x-r-md"
				itemClassName="flex w-full items-center justify-center"
				onNext={() => {
					setIndex(selectedIndex + 1);
				}}
				onPrevious={() => {
					setIndex(selectedIndex - 1);
				}}
				onScrollEnd={(items) => {
					// Using isFullyVisible should be enough, but it's a bit flaky.
					const index = items.findIndex(
						(item) => (item.visiblePercentage ?? 0) > 75,
					);
					if (index !== -1) {
						setIndex(index);
					}
				}}
				items={images.map((image) => (
					<Img
						key={image.src}
						src={image.src}
						alt={image.alt}
						width={272}
						height={272}
						// Default from Next is async, which causes flickering on every
						// render in Chrome (e.g. when sliding).
						decoding="sync"
					/>
				))}
			/>
			{images.length > 1 && (
				<ThumbnailList
					className="mt-4"
					carouselClassName="flex-wrap"
					isScreenReaderHidden
					size="sm"
					images={images}
					selectedImageIndex={selectedIndex}
					onThumbnailClick={(index) => {
						setIndex(index);
						carouselRef.current?.goToIndex(index, { animate: false });
					}}
				/>
			)}
		</div>
	);
}
ProductPopoverImage.displayName = 'ProductPopoverImage';
